import {
  Avatar,
  ConfirmationDialog,
  PageSpinner,
  useNotifications,
} from '@armhr/ui';
import { Key as KeyIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

import { useApi } from '../../hooks/useApi';
import { useApiData } from '../../hooks/useApiData';

type FilterState = {
  searchTerm: string;
};

const UserPasswordResetTab = () => {
  const { showNotification } = useNotifications();
  const api = useApi();
  const [filterState, setFilterState] = useState<FilterState>({
    searchTerm: '',
  });
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {}
  );
  const [confirmationDialog, setConfirmationDialog] = useState<{
    open: boolean;
    email: string;
    userName: string;
  }>({
    open: false,
    email: '',
    userName: '',
  });

  const { data: users, loading } = useApiData<Auth0UserForPasswordReset[]>(
    api.admin.getPasswordResetUsers
  );

  const filteredUsers = useMemo(
    () =>
      (users ?? []).filter((user) => {
        if (!user.email) return false;
        if (!filterState.searchTerm) return true;

        const searchLower = filterState.searchTerm.toLowerCase();
        return (
          user.name?.toLowerCase().includes(searchLower) ||
          user.email.toLowerCase().includes(searchLower)
        );
      }),
    [users, filterState.searchTerm]
  );

  const handleSendPasswordReset = useCallback(
    async (email: string) => {
      setLoadingStates((prev) => ({ ...prev, [email]: true }));

      try {
        await api.admin.sendPasswordReset({ email });

        showNotification({
          message: `Password reset email sent to ${email}`,
          severity: 'success',
        });
      } catch (error) {
        console.error('Error sending password reset email:', error);
        showNotification({
          message: 'Failed to send password reset email.',
          severity: 'error',
        });
      } finally {
        setLoadingStates((prev) => ({ ...prev, [email]: false }));
      }
    },
    [showNotification, api]
  );

  const handleSendPasswordResetClick = useCallback(
    (email: string, userName: string) => {
      setConfirmationDialog({
        open: true,
        email,
        userName,
      });
    },
    []
  );

  const handleConfirmSendPasswordReset = useCallback(() => {
    const { email } = confirmationDialog;
    handleSendPasswordReset(email);
    setConfirmationDialog((prev) => ({ ...prev, open: false }));
  }, [confirmationDialog, handleSendPasswordReset]);

  if (loading) return <PageSpinner />;

  return (
    <Box>
      <Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Select an employee below to send them a password reset link via
            email. They will receive instructions to create a new password.
          </Typography>
        </Alert>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <TextField
            placeholder="Search by name or email"
            value={filterState.searchTerm}
            onChange={(e) =>
              setFilterState((prev) => ({
                ...prev,
                searchTerm: e.target.value,
              }))
            }
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%' },
            }}
          />
        </Box>
      </Box>

      <Stack spacing={2}>
        {filteredUsers.map((user) => (
          <Box
            key={user.user_id}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}
            >
              <Avatar name={user.name!} />
              <Box>
                <Typography variant="subtitle1" fontWeight={500}>
                  {user.name ?? user.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.email}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={
                  loadingStates[user.email ?? ''] ? (
                    <CircularProgress size={16} />
                  ) : (
                    <KeyIcon />
                  )
                }
                onClick={() =>
                  handleSendPasswordResetClick(
                    user.email ?? '',
                    user.name ?? user.email ?? ''
                  )
                }
                disabled={loadingStates[user.email ?? '']}
                size="small"
              >
                Send Reset Link
              </Button>
            </Box>
          </Box>
        ))}
      </Stack>

      <ConfirmationDialog
        open={confirmationDialog.open}
        onClose={() =>
          setConfirmationDialog((prev) => ({ ...prev, open: false }))
        }
        onConfirm={handleConfirmSendPasswordReset}
        title="Send password reset email"
        message={
          <>
            Are you sure you want to send a password reset email to{' '}
            <strong>{confirmationDialog.userName}</strong>?
          </>
        }
        confirmText="Send reset email"
        cancelText="Cancel"
      />
    </Box>
  );
};

export default UserPasswordResetTab;
